import { CheckboxProps, Modal, Popover, Space, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { IDictionary } from '~utilities/IDictionary';
import './loan-payment-abas.less';
import { currencyFormatter } from '~utilities/formatters';
import { Key, TableRowSelection } from 'antd/lib/table/interface';
import { Dispatch } from 'redux';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import { warehousesSelector } from '~Warehouses/selectors';
import { warehousesListAction } from '~Warehouses/actions';
import IGroupedWarehouseLoanTransaction from '~LoanPaymentAbas/IGroupedWarehouseLoanTransaction';
import { loanPaymentAbasUnallocatedWarehouseLoanTransactionsSelector } from './selectors';
import {
    loanPaymentAbasAddAction,
    loanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction,
} from './actions';
import ICheckboxProps from '~utilities/ICheckboxProps';

export default function AddModal(props: {
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { onCancel, isOpen } = props;

    const [ selectedRowKeys, setSelectedRowKeys ] = useState<Key[]>([]);

    const unallocatedTransactions: IDictionary<IWarehouseLoanTransaction> = useSelector(loanPaymentAbasUnallocatedWarehouseLoanTransactionsSelector);
    const warehouses: IDictionary<IWarehouse> = useSelector(warehousesSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!unallocatedTransactions) {
            dispatch(loanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction());
        }
    }, [
        dispatch,
        unallocatedTransactions,
    ]);

    useEffect(() => {
        if (!warehouses) {
            dispatch(warehousesListAction());
        }
    }, [
        dispatch,
        warehouses,
    ]);

    // This needs to be a memo as it is a dependency in the onClickOk callback
    const groupedTransactions: IDictionary<IGroupedWarehouseLoanTransaction> = useMemo(() => {
        const groupedTransactions: IDictionary<IGroupedWarehouseLoanTransaction> = {};

        _.each(unallocatedTransactions ?? {}, (transaction: IWarehouseLoanTransaction) => {
            const key: string = transaction.loanTransactionUuid;

            if (!groupedTransactions[key]) {
                groupedTransactions[key] = {
                    loanCode: transaction.loanCode,
                    loanTransactionUuid: transaction.loanTransactionUuid,
                    loanUuid: transaction.loanUuid,
                    totalInterest: 0,
                    transactionUuids: [],
                    warehouseLoanTransactionTime: transaction.transactionTime,
                };
            }

            groupedTransactions[key].totalInterest += transaction.amountPrepaidInterest * -1;
            groupedTransactions[key].transactionUuids.push(transaction.uuid);
        });

        return groupedTransactions;
    }, [
        unallocatedTransactions,
    ]);

    const onClickOk: () => void = useCallback(() => {
        const selectedIds: string[] = [];

        selectedRowKeys.forEach((value: Key) => {
            // Transactions are grouped by loan transaction, so selectedRowKeys contains the loan transaction uuids
            // Grab all unallocated warehouse loan transactions that belong to that loan transaction
            const transactionUuids: string[] = groupedTransactions[value].transactionUuids;
            selectedIds.push(...transactionUuids);
        });

        dispatch(loanPaymentAbasAddAction(selectedIds));
        onCancel();
    }, [
        dispatch,
        groupedTransactions,
        onCancel,
        selectedRowKeys,
    ]);

    const onChangeSelectedRows: (rowKeys: Key[]) => void = useCallback((rowKeys: Key[]) => {
        setSelectedRowKeys(rowKeys);
    }, []);

    const getCheckboxProps: (row: IGroupedWarehouseLoanTransaction) => ICheckboxProps = useCallback((row: IGroupedWarehouseLoanTransaction) => ({
        className: null,
        disabled: false,
        key: row.loanTransactionUuid,
    }), []);

    if (!unallocatedTransactions || !warehouses) {
        return (
            <Modal
                okText='Add'
                open={isOpen}
                title='Add Loan Payment ABA'
                width={1000}
                wrapClassName='loan-payment-abas-add-modal'
            >
                <Spin />
            </Modal>
        );
    }

    const rowSelection: TableRowSelection<IGroupedWarehouseLoanTransaction> = {
        getCheckboxProps,
        onChange: onChangeSelectedRows,
        selectedRowKeys,
    };

    const columns: ColumnsType<IGroupedWarehouseLoanTransaction> = [
        {
            dataIndex: 'warehouseLoanTransactionTime',
            defaultSortOrder: 'descend',
            render: (warehouseLoanTransactionTime: string) => dayjs(warehouseLoanTransactionTime).format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a: IGroupedWarehouseLoanTransaction, b: IGroupedWarehouseLoanTransaction) => dayjs(a.warehouseLoanTransactionTime) > dayjs(b.warehouseLoanTransactionTime) ? 1 : -1,
            title: 'Transaction Time',
            width: 200,
        },
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IGroupedWarehouseLoanTransaction): ReactNode => {
                const content: ReactNode = _.map(row.transactionUuids, (transactionUuid: string) => (
                    <p key={transactionUuid}>
                        {warehouses[unallocatedTransactions[transactionUuid].warehouseUuid].type}: {currencyFormatter.format(unallocatedTransactions[transactionUuid].amountPrepaidInterest * -1)}
                    </p>
                ));

                return (
                    <Space>
                        <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>
                        {row.transactionUuids.length > 1 && <Popover content={content}><Tag>Split</Tag></Popover>}
                    </Space>
                );
            },
            title: 'Loan',
        },
        {
            dataIndex: 'totalInterest',
            render: (totalInterest: number) => currencyFormatter.format(totalInterest),
            title: 'Interest',
            width: 200,
        },
    ];

    return (
        <Modal
            okButtonProps={{ disabled: !selectedRowKeys.length }}
            okText='Add'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Add Loan Payment ABA'
            width={1000}
            wrapClassName='loan-payment-abas-add-modal'
        >
            <Table
                columns={columns}
                dataSource={_.values(groupedTransactions)}
                pagination={false}
                rowKey='loanTransactionUuid'
                rowSelection={rowSelection}
                scroll={{ y: 600 }}
                size='middle'
            />
        </Modal>
    );
}
